import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { EVENTS } from '@apps/web/lib/events';
import { sanitizeReturnToPath } from '@apps/web/lib/post_login_redirect';
import { usePostHog } from '@apps/web/lib/posthog';
import { sameOriginRedirectTarget } from '@apps/web/lib/same_origin_redirect';
import {
    ApiError,
    type ShortLinkResolution,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

function returnToPathForCurrentOrigin(href: string): string {
    return sanitizeReturnToPath(href, window.location.origin) ?? '/';
}

type ShortLinkLoaderData =
    | { readonly kind: 'resolved'; readonly resolution: ShortLinkResolution }
    | { readonly kind: 'unavailable' }
    | { readonly kind: 'signed_out' };

// Unknown, expired, and other-org codes all get the same 404 by design, and a
// 401 is a session that expired mid-tap. Return those expected outcomes rather
// than throwing them: a thrown loader error reaches the error boundary, which
// reports it to Sentry as a crash.
export async function loadShortLink(
    code: string,
): Promise<ShortLinkLoaderData> {
    try {
        const resolution = await getApiClient().get({
            url: '/short-links/{code}',
            pathParams: { code },
        });
        return { kind: 'resolved', resolution };
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
            return { kind: 'unavailable' };
        }
        if (error instanceof ApiError && error.status === 401) {
            return { kind: 'signed_out' };
        }
        throw error;
    }
}

export const Route = createFileRoute('/r/$code')({
    beforeLoad: ({ context, location }) => {
        // Carry the link's path through sign-in so a logged-out tap returns
        // here after authenticating, instead of landing on home. Mirrors the
        // _app guard; sign-in sanitizes the value and Kratos allowlists it.
        if (!context.sessionState?.session) {
            throw redirect({
                to: '/sign-in',
                search: {
                    return_to: returnToPathForCurrentOrigin(location.href),
                },
            });
        }
    },
    loader: ({ params }) => loadShortLink(params.code),
    component: ShortLinkPage,
    errorComponent: ShortLinkError,
});

function ShortLinkPage() {
    const result = Route.useLoaderData();
    switch (result.kind) {
        case 'resolved':
            return <ShortLinkRedirect resolution={result.resolution} />;
        case 'unavailable':
            return (
                <ShortLinkStatus>
                    This link is no longer available.
                </ShortLinkStatus>
            );
        case 'signed_out':
            return <ShortLinkSignIn />;
        default:
            return assert.never(result);
    }
}

function ShortLinkRedirect({
    resolution,
}: {
    readonly resolution: ShortLinkResolution;
}) {
    const posthog = usePostHog();
    const redirected = useRef(false);
    useEffect(() => {
        if (redirected.current) {
            return;
        }
        // Never hand a raw value to window.location — only a re-resolved
        // same-origin URL, so the destination can't become an open redirect.
        const redirect = () => {
            if (redirected.current) {
                return;
            }
            redirected.current = true;
            window.location.href = sameOriginRedirectTarget(
                resolution.destination_path,
                window.location.origin,
            );
        };
        // Keep the attribution event alive through a cold SDK load, but cap the
        // wait so navigation never depends on an analytics vendor.
        const fallback = setTimeout(redirect, 2000);
        void posthog
            .captureAndFlush(
                EVENTS.SHORT_LINK_OPENED,
                {
                    channel: resolution.channel,
                    campaign: resolution.campaign,
                    utm_source: 'phoebe',
                    utm_medium: resolution.channel,
                    utm_campaign: resolution.campaign,
                    organization_id: resolution.organization_id,
                },
                { send_instantly: true },
            )
            .then(redirect);
        return () => clearTimeout(fallback);
    }, [resolution, posthog]);
    return <ShortLinkStatus>Opening…</ShortLinkStatus>;
}

function ShortLinkSignIn() {
    useEffect(() => {
        window.location.href = '/sign-in';
    }, []);
    return <ShortLinkStatus>Signing you in…</ShortLinkStatus>;
}

function ShortLinkError() {
    // The loader returns the expected outcomes, so anything thrown is
    // unexpected or transient and should invite a retry rather than claim the
    // link is gone.
    return (
        <ShortLinkStatus>
            Something went wrong opening this link. Please try again.
        </ShortLinkStatus>
    );
}

function ShortLinkStatus({ children }: { readonly children: ReactNode }) {
    return (
        <div className="bg-background text-muted-foreground flex min-h-screen items-center justify-center">
            {children}
        </div>
    );
}
